import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Check, Warning, X } from "@phosphor-icons/react";

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function NotificationBell() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const resp = await api.get("/notifications?limit=15");
      setNotifications(resp.data.notifications || []);
      setUnread(resp.data.unread_count || 0);
    } catch { /* silent */ }
  }, [api]);

  // Initial fetch + polling every 60s
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      await api.post("/notifications/mark-read", { notification_id: notif.notification_id });
      setNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    }
    setOpen(false);
    navigate("/history");
  };

  const markAllRead = async () => {
    await api.post("/notifications/mark-read", {});
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={18} weight={unread > 0 ? "fill" : "regular"} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid="notification-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-80 bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-lg shadow-xl z-50 overflow-hidden"
            data-testid="notification-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
              <span className="text-xs font-medium text-[var(--text-primary)]">Notifications</span>
              {unread > 0 && (
                <button onClick={markAllRead} className="text-[10px] text-[#FF4400] hover:text-[#FF6633] font-medium transition-colors" data-testid="mark-all-read">
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-72 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="py-8 text-center text-xs text-[var(--text-muted)]" data-testid="no-notifications">No notifications yet</div>
              ) : (
                notifications.map((notif, i) => (
                  <button
                    key={notif.notification_id || i}
                    onClick={() => handleNotifClick(notif)}
                    className={`w-full text-left px-4 py-3 border-b border-[var(--border)] hover:bg-[var(--bg-hover)] transition-colors ${!notif.read ? "bg-[#FF4400]/3" : ""}`}
                    data-testid={`notification-item-${i}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${notif.decision === "approved" ? "bg-emerald-500/10" : "bg-amber-500/10"}`}>
                        {notif.decision === "approved"
                          ? <Check size={12} className="text-emerald-400" weight="bold" />
                          : <Warning size={12} className="text-amber-400" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[var(--text-primary)] leading-snug">
                          {notif.decision === "approved"
                            ? "Client approved your content"
                            : "Client requested changes"
                          }
                        </p>
                        {notif.client_note && (
                          <p className="text-[10px] text-[var(--text-muted)] mt-0.5 truncate italic">"{notif.client_note}"</p>
                        )}
                        <p className="text-[10px] text-[var(--text-muted)] mt-1">{formatTime(notif.created_at)}</p>
                      </div>
                      {!notif.read && <div className="w-2 h-2 rounded-full bg-[#FF4400] shrink-0 mt-1.5" />}
                    </div>
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
